<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <span class="back" @click="close">
          <i class="ph ph-arrow-left"></i>
        </span>
        <span class="title">게시물</span>
        <span class="more" v-if="isMyPost" @click="showMenu = !showMenu">
          <i class="ph ph-dots-three"></i>
        </span>
        <span class="more" v-else></span>
        
        <!-- Dropdown Menu -->
        <div class="dropdown-menu" v-if="showMenu" @click.stop>
          <button @click="editPost">
            <i class="ph ph-pencil-simple"></i>
            수정
          </button>
          <button @click="deletePost" class="danger">
            <i class="ph ph-trash"></i>
            삭제
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="content">
        <!-- Author -->
        <div class="author-section">
          <div class="avatar">
            <i class="ph-fill ph-user"></i>
          </div>
          <div class="author-info">
            <span class="username">{{ post.mb_id }}</span>
            <span class="location" v-if="post.location">{{ post.location }}</span>
          </div>
        </div>

        <!-- Image Carousel with Swipe -->
        <div 
          class="image-section" 
          v-if="images.length > 0"
          ref="carouselRef"
        >
          <div class="carousel">
            <div 
              class="carousel-track" 
              :style="carouselStyle"
            >
              <div class="carousel-slide" v-for="(img, idx) in images" :key="idx">
                <img :src="img" alt="게시물 이미지" />
              </div>
            </div>
          </div>
          
          <!-- Carousel Indicators -->
          <div class="carousel-indicators" v-if="images.length > 1">
            <span 
              v-for="(_, idx) in images" 
              :key="idx"
              :class="['indicator', { active: currentImageIndex === idx }]"
              @click="currentImageIndex = idx"
            ></span>
          </div>

          <!-- Carousel Arrows (PC용) -->
          <button 
            class="carousel-arrow prev" 
            v-if="images.length > 1 && currentImageIndex > 0"
            @click="currentImageIndex--"
          >
            <i class="ph ph-caret-left"></i>
          </button>
          <button 
            class="carousel-arrow next" 
            v-if="images.length > 1 && currentImageIndex < images.length - 1"
            @click="currentImageIndex++"
          >
            <i class="ph ph-caret-right"></i>
          </button>

          <!-- 이미지 크게 보기 버튼 -->
          <button class="expand-btn" @click="openFullImage(currentImageIndex)">
            <i class="ph ph-arrows-out"></i>
          </button>
        </div>

        <!-- Actions -->
        <div class="actions-section">
          <div class="action-left">
            <button class="action-btn" :class="{ active: isLiked }" @click="toggleLike">
              <i :class="isLiked ? 'ph-fill ph-heart' : 'ph ph-heart'"></i>
            </button>
            <button class="action-btn" @click="focusCommentInput">
              <i class="ph ph-chat-circle"></i>
            </button>
            <button class="action-btn">
              <i class="ph ph-paper-plane-tilt"></i>
            </button>
          </div>
          <button class="action-btn" :class="{ active: isBookmarked }" @click="isBookmarked = !isBookmarked">
            <i :class="isBookmarked ? 'ph-fill ph-bookmark-simple' : 'ph ph-bookmark-simple'"></i>
          </button>
        </div>

        <!-- Stats -->
        <div class="stats-section">
          <p class="likes" v-if="likeCount > 0">
            공감 <strong>{{ likeCount }}개</strong>
          </p>
        </div>

        <!-- Caption -->
        <div class="caption-section">
          <p class="caption">
            <strong class="author">{{ post.mb_id }}</strong>
            <span class="title-text">{{ post.title }}</span>
          </p>
          <p class="body-text" v-if="post.content">{{ post.content }}</p>
          <span class="timestamp">{{ formatDate(post.created_at) }}</span>
        </div>

        <!-- Comments -->
        <div class="comments-section">
          <p class="comments-count" v-if="comments.length > 0">
            댓글 {{ comments.length }}개
          </p>

          <!-- Comment List - 댓글이 있을 때만 표시 -->
          <div class="comment-list" v-if="comments.length > 0">
            <div class="comment-item" v-for="comment in comments" :key="comment.idx">
              <div class="comment-avatar">
                <i class="ph-fill ph-user"></i>
              </div>
              <div class="comment-body">
                <p class="comment-text">
                  <strong>{{ comment.mb_id }}</strong>
                  {{ comment.content }}
                </p>
                <img v-if="comment.sticker" :src="comment.sticker" class="comment-sticker" />
                <div class="comment-meta">
                  <span class="comment-time">{{ formatDate(comment.created_at) }}</span>
                  <button class="comment-reply">답글 달기</button>
                </div>
              </div>
              <button class="comment-like">
                <i class="ph ph-heart"></i>
              </button>
            </div>
          </div>

          <!-- Empty Comments -->
          <div class="empty-comments" v-else>
            <p>아직 댓글이 없습니다</p>
            <span>첫 번째 댓글을 남겨보세요</span>
          </div>
        </div>
      </div>

      <!-- Comment Input -->
      <div class="comment-input-section">
        <div class="input-avatar">
          <i class="ph-fill ph-user"></i>
        </div>
        <input 
          type="text" 
          ref="commentInput"
          v-model="newComment" 
          placeholder="댓글 달기..."
          @keyup.enter="submitComment"
        />
        <button class="sticker-toggle-btn" @click="showSticker = true">
          <i class="ph ph-sticker"></i>
        </button>
        <button 
          class="post-btn" 
          :class="{ active: newComment.trim() || selectedSticker }"
          @click="submitComment"
          :disabled="!newComment.trim() && !selectedSticker"
        >
          게시
        </button>
      </div>
      <!-- 선택된 스티커 미리보기 -->
      <div v-if="selectedSticker" class="sns-sticker-preview">
        <img :src="selectedSticker" />
        <span class="sticker-remove" @click="selectedSticker = ''">&times;</span>
      </div>
      <StickerSelector v-model="showSticker" @select="onStickerSelect" />

      <!-- Full Image Viewer -->
      <div 
        class="image-viewer" 
        v-if="showFullImage" 
        @click="showFullImage = false"
      >
        <button class="viewer-close">
          <i class="ph ph-x"></i>
        </button>
        <div 
          class="viewer-content" 
          @click.stop
          @touchstart="viewerTouchStart"
          @touchend="viewerTouchEnd"
        >
          <img :src="images[fullImageIndex]" alt="이미지" />
        </div>
        <div class="viewer-indicators" v-if="images.length > 1">
          <span 
            v-for="(_, idx) in images" 
            :key="idx"
            :class="['indicator', { active: fullImageIndex === idx }]"
            @click.stop="fullImageIndex = idx"
          ></span>
        </div>
        <button 
          class="viewer-arrow prev" 
          v-if="images.length > 1 && fullImageIndex > 0"
          @click.stop="fullImageIndex--"
        >
          <i class="ph ph-caret-left"></i>
        </button>
        <button 
          class="viewer-arrow next" 
          v-if="images.length > 1 && fullImageIndex < images.length - 1"
          @click.stop="fullImageIndex++"
        >
          <i class="ph ph-caret-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted, onUnmounted, nextTick } from "vue"
import StickerSelector from "@/components/dialogs/StickerSelector.vue"

export default {
  components: { StickerSelector },
  props: {
    oth_showSnsView: Function,
    oth_showSnsEdit: Function,
    postIdx: { required: true }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const post = ref({})
    const images = ref([])
    const comments = ref([])
    const newComment = ref('')
    const showMenu = ref(false)
    const isLiked = ref(false)
    const isBookmarked = ref(false)
    const likeCount = ref(0)
    const currentImageIndex = ref(0)
    const showFullImage = ref(false)
    const fullImageIndex = ref(0)
    const commentInput = ref(null)
    const carouselRef = ref(null)
    const showSticker = ref(false)
    const selectedSticker = ref('')

    function onStickerSelect(url) {
      selectedSticker.value = url
    }
    
    // 데이터 변경 여부 추적
    const hasDataChanged = ref(false)

    // 스와이프 관련
    const touchStartX = ref(0)
    const touchStartY = ref(0)
    const touchDeltaX = ref(0)
    const isSwiping = ref(false)
    const isHorizontalSwipe = ref(false)

    const isMyPost = computed(() => post.value.mb_id === mb_id)

    // 캐러셀 스타일
    const carouselStyle = computed(() => {
      const baseTranslate = -currentImageIndex.value * 100
      const dragPercent = isSwiping.value && isHorizontalSwipe.value 
        ? (touchDeltaX.value / (carouselRef.value?.offsetWidth || 300)) * 100 
        : 0
      return {
        transform: `translateX(calc(${baseTranslate}% + ${dragPercent}%))`,
        transition: isSwiping.value ? 'none' : 'transform 0.3s ease'
      }
    })

    // 터치 이벤트 핸들러
    const onTouchStart = (e) => {
      touchStartX.value = e.touches[0].clientX
      touchStartY.value = e.touches[0].clientY
      touchDeltaX.value = 0
      isSwiping.value = true
      isHorizontalSwipe.value = false
    }

    const onTouchMove = (e) => {
      if (!isSwiping.value) return
      
      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const diffX = currentX - touchStartX.value
      const diffY = currentY - touchStartY.value

      // 첫 움직임에서 방향 결정
      if (!isHorizontalSwipe.value && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
        isHorizontalSwipe.value = Math.abs(diffX) > Math.abs(diffY)
      }

      // 수평 스와이프면 스크롤 막고 캐러셀 이동
      if (isHorizontalSwipe.value) {
        e.preventDefault()
        touchDeltaX.value = diffX
      }
    }

    const onTouchEnd = () => {
      if (!isSwiping.value) return
      
      const threshold = 50
      
      if (isHorizontalSwipe.value) {
        if (touchDeltaX.value < -threshold && currentImageIndex.value < images.value.length - 1) {
          currentImageIndex.value++
        } else if (touchDeltaX.value > threshold && currentImageIndex.value > 0) {
          currentImageIndex.value--
        }
      }
      
      isSwiping.value = false
      isHorizontalSwipe.value = false
      touchDeltaX.value = 0
    }

    // 터치 이벤트 등록/해제
    const setupTouchEvents = () => {
      if (!carouselRef.value) return
      carouselRef.value.addEventListener('touchstart', onTouchStart, { passive: true })
      carouselRef.value.addEventListener('touchmove', onTouchMove, { passive: false })
      carouselRef.value.addEventListener('touchend', onTouchEnd, { passive: true })
    }

    const removeTouchEvents = () => {
      if (!carouselRef.value) return
      carouselRef.value.removeEventListener('touchstart', onTouchStart)
      carouselRef.value.removeEventListener('touchmove', onTouchMove)
      carouselRef.value.removeEventListener('touchend', onTouchEnd)
    }

    // 이미지 뷰어 스와이프
    const viewerStartX = ref(0)
    const viewerTouchStart = (e) => {
      viewerStartX.value = e.touches[0].clientX
    }
    const viewerTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX
      const diff = viewerStartX.value - endX
      const threshold = 50
      
      if (diff > threshold && fullImageIndex.value < images.value.length - 1) {
        fullImageIndex.value++
      } else if (diff < -threshold && fullImageIndex.value > 0) {
        fullImageIndex.value--
      }
    }

    const loadPost = async () => {
      await sql_query(`
        UPDATE sns_post SET view_count = view_count + 1 WHERE idx = ${props.postIdx}
      `)

      const rows = await sql_query(`
        SELECT idx, mb_id, title, content, images, view_count, created_at
        FROM sns_post WHERE idx = ${props.postIdx}
      `)
      
      if (rows.length > 0) {
        post.value = rows[0]
        images.value = rows[0].images ? JSON.parse(rows[0].images) : []
      }
    }

    // 좋아요 수 & 내가 좋아요 했는지 확인
    const loadLikes = async () => {
      // 좋아요 수
      const countRows = await sql_query(`
        SELECT COUNT(*) as cnt FROM likes 
        WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx}
      `)
      likeCount.value = countRows[0]?.cnt || 0
      
      // 내가 좋아요 했는지
      const myLike = await sql_query(`
        SELECT idx FROM likes 
        WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx} AND mb_id = '${mb_id}'
      `)
      isLiked.value = myLike.length > 0
    }

    // 댓글 로드 (공용 comment 테이블 사용)
    const loadComments = async () => {
      try {
        const rows = await sql_query(`
          SELECT idx, mb_id, content, sticker, created_at
          FROM comment
          WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx} AND status = 'normal'
          ORDER BY created_at ASC
        `)
        if (Array.isArray(rows) && rows.length > 0 && rows[0].idx !== undefined) {
          comments.value = rows
        } else {
          comments.value = []
        }
      } catch (e) {
        comments.value = []
      }
    }

    // 댓글 등록 (공용 comment 테이블 사용)
    const submitComment = async () => {
      if (!newComment.value.trim()) return

      try {
        const stickerVal = selectedSticker.value || ''
        await sql_query(`
          INSERT INTO comment (mb_id, target_table, target_idx, content, sticker)
          VALUES ('${mb_id}', 'sns_post', ${props.postIdx}, '${newComment.value.replace(/'/g, "''")}', '${stickerVal}')
        `)

        newComment.value = ''
        selectedSticker.value = ''
        hasDataChanged.value = true
        loadComments()
      } catch (e) {
        alert('댓글을 등록할 수 없습니다')
      }
    }

    // 좋아요 토글 (공용 likes 테이블 사용)
    const toggleLike = async () => {
      if (isLiked.value) {
        // 좋아요 취소
        await sql_query(`
          DELETE FROM likes 
          WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx} AND mb_id = '${mb_id}'
        `)
      } else {
        // 좋아요
        await sql_query(`
          INSERT INTO likes (mb_id, target_table, target_idx)
          VALUES ('${mb_id}', 'sns_post', ${props.postIdx})
        `)
      }
      hasDataChanged.value = true
      loadLikes()
    }

    const editPost = () => {
      showMenu.value = false
      props.oth_showSnsEdit(true, { editIdx: props.postIdx })
    }

    const deletePost = async () => {
      if (!confirm('이 게시물을 삭제하시겠어요?')) return
      
      showMenu.value = false
      
      // 댓글 삭제
      try {
        await sql_query(`
          DELETE FROM comment WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx}
        `)
      } catch (e) { console.log(e) }
      
      // 좋아요 삭제
      try {
        await sql_query(`
          DELETE FROM likes WHERE target_table = 'sns_post' AND target_idx = ${props.postIdx}
        `)
      } catch (e) { console.log(e) }
      
      // 게시물 삭제
      await sql_query(`
        DELETE FROM sns_post WHERE idx = ${props.postIdx} AND mb_id = '${mb_id}'
      `)
      
      props.oth_showSnsView(false, { refresh: true })
    }

    const focusCommentInput = () => {
      nextTick(() => {
        commentInput.value?.focus()
      })
    }

    const openFullImage = (idx) => {
      fullImageIndex.value = idx
      showFullImage.value = true
    }

    const close = () => {
      props.oth_showSnsView(false, { refresh: hasDataChanged.value })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일`
      if (diff < 2592000000) return `${Math.floor(diff / 604800000)}주`
      
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    onMounted(() => {
      loadPost()
      loadLikes()
      loadComments()
      nextTick(() => {
        setupTouchEvents()
      })
    })

    onUnmounted(() => {
      removeTouchEvents()
    })

    return {
      post,
      images,
      comments,
      newComment,
      showMenu,
      isLiked,
      isBookmarked,
      isMyPost,
      likeCount,
      currentImageIndex,
      showFullImage,
      fullImageIndex,
      commentInput,
      carouselRef,
      carouselStyle,
      viewerTouchStart,
      viewerTouchEnd,
      showSticker,
      selectedSticker,
      onStickerSelect,
      submitComment,
      toggleLike,
      editPost,
      deletePost,
      focusCommentInput,
      openFullImage,
      close,
      formatDate
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 470px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
  position: relative;
}

.header > .back,
.header > .more {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > .back > i,
.header > .more > i {
  font-size: 24px;
  color: var(--color-text-primary);
}

.header > .title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.dropdown-menu {
  position: absolute;
  top: 50px;
  right: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  z-index: 10;
}

.dropdown-menu button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  border: none;
  background: none;
  text-align: left;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
}

.dropdown-menu button > i {
  font-size: 18px;
}

.dropdown-menu button:hover {
  background: var(--color-background-light);
}

.dropdown-menu button.danger {
  color: var(--color-special-warning);
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
}

/* Author Section */
.author-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #833AB4, #FD1D1D, #F77737);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
}

.avatar > i {
  width: 100%;
  height: 100%;
  background: var(--color-background);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--color-text-secondary);
}

.author-info {
  display: flex;
  flex-direction: column;
}

.username {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.location {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* Image Section */
.image-section {
  position: relative;
  width: 100%;
  background: var(--color-background-light);
  user-select: none;
  -webkit-user-select: none;
}

.carousel {
  width: 100%;
  overflow: hidden;
}

.carousel-track {
  display: flex;
  will-change: transform;
}

.carousel-slide {
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 1;
}

.carousel-slide > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  -webkit-user-drag: none;
  pointer-events: auto;
}

.carousel-indicators {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background 0.2s;
}

.indicator.active {
  background: #fff;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-arrow.prev { left: 12px; }
.carousel-arrow.next { right: 12px; }

.carousel-arrow > i {
  font-size: 16px;
  color: #262626;
}

/* Expand Button */
.expand-btn {
  position: absolute;
  bottom: 16px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expand-btn > i {
  font-size: 16px;
  color: #fff;
}

/* Actions Section */
.actions-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}

.action-left {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.action-btn:active {
  transform: scale(0.9);
}

.action-btn > i {
  font-size: 24px;
  color: var(--color-text-primary);
}

.action-btn.active > i {
  color: #ed4956;
}

/* Stats Section */
.stats-section {
  padding: 0 16px;
}

.likes {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
}

/* Caption Section */
.caption-section {
  padding: 0 16px 16px;
}

.caption {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.author {
  margin-right: 6px;
}

.body-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 8px 0;
  line-height: 1.6;
  white-space: pre-wrap;
}

.timestamp {
  font-size: 10px;
  color: var(--color-text-third);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Comments Section */
.comments-section {
  padding: 16px;
  border-top: 1px solid var(--color-background-light);
}

.comments-count {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 16px 0;
}

.comment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.comment-item {
  display: flex;
  gap: 12px;
}

.comment-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.comment-avatar > i {
  font-size: 14px;
  color: var(--color-text-white);
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
}

.comment-text > strong {
  margin-right: 6px;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.comment-time {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

.comment-reply {
  border: none;
  background: none;
  padding: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-third);
  cursor: pointer;
}

.comment-like {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 4px;
}

.comment-like > i {
  font-size: 12px;
  color: var(--color-text-third);
}

.empty-comments {
  text-align: center;
  padding: 24px 0;
}

.empty-comments > p {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
}

.empty-comments > span {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

/* Comment Input Section */
.comment-input-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.input-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.input-avatar > i {
  font-size: 12px;
  color: var(--color-text-white);
}

.comment-input-section > input {
  flex: 1;
  border: none;
  background: none;
  padding: 8px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.comment-input-section > input::placeholder {
  color: var(--color-text-third);
}

.post-btn {
  border: none;
  background: none;
  padding: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
  cursor: pointer;
  opacity: 0.3;
  transition: opacity 0.2s;
}

.post-btn.active {
  opacity: 1;
}

.post-btn:disabled {
  cursor: default;
}

/* Image Viewer */
.image-viewer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.viewer-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close > i {
  font-size: 28px;
  color: #fff;
}

.viewer-content {
  max-width: 90%;
  max-height: 90%;
}

.viewer-content > img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

.viewer-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-arrow.prev { left: 16px; }
.viewer-arrow.next { right: 16px; }

.viewer-arrow > i {
  font-size: 24px;
  color: #fff;
}

.viewer-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
}

.viewer-indicators > .indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.viewer-indicators > .indicator.active {
  background: #fff;
}

.sticker-toggle-btn {
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticker-toggle-btn > i {
  font-size: 22px;
  color: var(--color-text-secondary);
}

.sns-sticker-preview {
  position: relative;
  display: inline-block;
  padding: 4px 16px 8px;
}

.sns-sticker-preview img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--color-background-light, #f5f5f5);
}

.sticker-remove {
  position: absolute;
  top: 0;
  right: 10px;
  width: 20px;
  height: 20px;
  background: #e74c3c;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
}

.comment-sticker {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  margin-top: 4px;
}
</style>